
import { CHAT_FONT_OPTION_GROUPS, chatFontFamily, normalizeChatFontKey, preRangePinnedWeight } from 'lib/chatFonts'

export const MERCH_FONT_KEYS: readonly string[] = [
  'DOTO',
  'BITCOUNT_GRID_SINGLE',
  'VCR_OSD_MONO'
]

export const MERCH_BASE_FONT_STACK = "'Bitcount Grid Single', monospace"

export const MERCH_FONT_OPTION_GROUPS = CHAT_FONT_OPTION_GROUPS
  .map((group) => ({
    ...group,
    options: group.options.filter((o) => MERCH_FONT_KEYS.includes(o.key))
  }))
  .filter((group) => group.options.length > 0)

export const normalizeMerchFontKey = (v: unknown): string => {
  if (typeof v !== 'string' || v === '') return 'BITCOUNT_GRID_SINGLE'
  const normalized = normalizeChatFontKey(v)
  return MERCH_FONT_KEYS.includes(normalized) ? normalized : 'BITCOUNT_GRID_SINGLE'
}

export const merchFontStack = (key: string): string =>
  chatFontFamily(key) ?? MERCH_BASE_FONT_STACK

export const MERCH_FONT_FORCED_WEIGHTS: Readonly<Record<string, number>> = {
  BITCOUNT_GRID_SINGLE: 400
}

export const MERCH_PART_DESIGN_WEIGHTS = {
  title: 900,
  name: 700,
  price: 800
} as const

export type MerchWeightPart = keyof typeof MERCH_PART_DESIGN_WEIGHTS

export const merchFontWeight = (fontKey: string, part: MerchWeightPart): number =>
  MERCH_FONT_FORCED_WEIGHTS[fontKey] ??
  preRangePinnedWeight(fontKey, MERCH_PART_DESIGN_WEIGHTS[part])

export const MERCH_PRICE_UP_COLOR = '#63f817'
export const MERCH_PRICE_DOWN_COLOR = '#ff0000'
export const MERCH_PRICE_FLAT_COLOR = '#9a9a9a'

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

export const merchColorOr = (v: unknown, fallback: string): string =>
  typeof v === 'string' && (HEX_COLOR_RE.test(v) || v === 'transparent')
    ? v
    : fallback

export const MERCH_CHANGE_ANIMATIONS = ['FLASH', 'BLINK', 'TWEEN', 'NONE'] as const
export type MerchChangeAnimation = (typeof MERCH_CHANGE_ANIMATIONS)[number]

export const merchChangeAnimationOr = (v: unknown, fallback: MerchChangeAnimation): MerchChangeAnimation =>
  v === 'FLASH' || v === 'BLINK' || v === 'TWEEN' || v === 'NONE' ? v : fallback

export const MAX_MERCH_CORNER_RADIUS = 64

export const merchRadiusOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_MERCH_CORNER_RADIUS
    ? v
    : fallback

export const MAX_MERCH_OUTER_BORDER_PX = 20

export const merchOuterBorderOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_MERCH_OUTER_BORDER_PX
    ? v
    : fallback

export const MIN_MERCH_TWEEN_MS = 100
export const MAX_MERCH_TWEEN_MS = 5000

export const merchTweenMsOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= MIN_MERCH_TWEEN_MS && v <= MAX_MERCH_TWEEN_MS
    ? v
    : fallback

export const MAX_MERCH_TEXT_GLOW_PX = 40

export const merchTextGlowPxOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_MERCH_TEXT_GLOW_PX
    ? v
    : fallback

export const MAX_MERCH_BOARD_PAD_X_PX = 200

export const merchPadXOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_MERCH_BOARD_PAD_X_PX
    ? v
    : fallback

export const MIN_MERCH_PHOTO_HEIGHT_PX = 20
export const MAX_MERCH_PHOTO_HEIGHT_PX = 400

export const merchPhotoHeightOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isInteger(v) &&
  v >= MIN_MERCH_PHOTO_HEIGHT_PX && v <= MAX_MERCH_PHOTO_HEIGHT_PX
    ? v
    : null

export const MIN_MERCH_COLLAPSE_X_PX = 12
export const MAX_MERCH_COLLAPSE_X_PX = 64

export const merchCollapseXSizeOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) &&
  v >= MIN_MERCH_COLLAPSE_X_PX && v <= MAX_MERCH_COLLAPSE_X_PX
    ? v
    : fallback

export interface ResolvedMerchStyle {
  titleColor: string
  titleFontKey: string
  productTitleColor: string
  productTitleFontKey: string
  priceFontKey: string
  boardBgColor: string
  rowBgColor: string
  tileBgColor: string
  outerBgColor: string
  outerCornerRadius: number
  boardCornerRadius: number
  outerBorderColor: string
  outerBorderWidth: number
  tabOutlineColor: string
  tabTextColor: string
  changeAnimation: MerchChangeAnimation
  tweenMs: number
  showVariants: boolean
  squarePhotos: boolean
  offsiteMarkColor: string
  textGlowPx: number
  textGlowColor: string
  textGlowArrows: boolean
  outerPadX: number
  boardPadX: number
  shopLabelColor: string
  ctaColor: string
  outerPadTop: number
  outerPadBottom: number
  boardBgImage: string
  productImageHeight: number | null
  rowBorderColor: string
  collapseXSize: number
  collapseXColor: string
}

export const MERCH_STYLE_DEFAULTS: ResolvedMerchStyle = {
  titleColor: '#ffffff',
  titleFontKey: 'BITCOUNT_GRID_SINGLE',
  productTitleColor: '#ffffff',
  productTitleFontKey: 'BITCOUNT_GRID_SINGLE',
  priceFontKey: 'BITCOUNT_GRID_SINGLE',
  boardBgColor: '#171717',
  rowBgColor: '#1f1f1f',
  tileBgColor: '#ffffff',
  outerBgColor: '#00000000',
  outerCornerRadius: 28,
  boardCornerRadius: 14,
  outerBorderColor: '#ffffff',
  outerBorderWidth: 0,
  tabOutlineColor: '#63f817',
  tabTextColor: '#888886',
  changeAnimation: 'FLASH',
  tweenMs: 600,
  showVariants: true,
  squarePhotos: false,
  offsiteMarkColor: '',
  textGlowPx: 0,
  textGlowColor: '#ffffff40',
  textGlowArrows: true,
  outerPadX: 30,
  boardPadX: 30,
  shopLabelColor: '#63f817',
  ctaColor: '#63f817',
  outerPadTop: 45,
  outerPadBottom: 30,
  boardBgImage: '',
  productImageHeight: null,
  rowBorderColor: '#2c2c2c',
  collapseXSize: 28,
  collapseXColor: ''
}

export const resolveMerchStyle = (raw: unknown): ResolvedMerchStyle => {
  const obj: Record<string, unknown> =
    raw !== null && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  const fontKey = (v: unknown): string =>
    typeof v === 'string' && v !== '' ? v : 'BITCOUNT_GRID_SINGLE'
  return {
    titleColor: merchColorOr(obj.titleColor, MERCH_STYLE_DEFAULTS.titleColor),
    titleFontKey: fontKey(obj.titleFontKey),
    productTitleColor: merchColorOr(obj.productTitleColor, MERCH_STYLE_DEFAULTS.productTitleColor),
    productTitleFontKey: fontKey(obj.productTitleFontKey),
    priceFontKey: fontKey(obj.priceFontKey),
    boardBgColor: merchColorOr(obj.boardBgColor, MERCH_STYLE_DEFAULTS.boardBgColor),
    rowBgColor: merchColorOr(obj.rowBgColor, MERCH_STYLE_DEFAULTS.rowBgColor),
    tileBgColor: merchColorOr(obj.tileBgColor, MERCH_STYLE_DEFAULTS.tileBgColor),
    outerBgColor: merchColorOr(obj.outerBgColor, MERCH_STYLE_DEFAULTS.outerBgColor),
    outerCornerRadius: merchRadiusOr(obj.outerCornerRadius, MERCH_STYLE_DEFAULTS.outerCornerRadius),
    boardCornerRadius: merchRadiusOr(obj.boardCornerRadius, MERCH_STYLE_DEFAULTS.boardCornerRadius),
    outerBorderColor: merchColorOr(obj.outerBorderColor, MERCH_STYLE_DEFAULTS.outerBorderColor),
    outerBorderWidth: merchOuterBorderOr(obj.outerBorderWidth, MERCH_STYLE_DEFAULTS.outerBorderWidth),
    tabOutlineColor: merchColorOr(obj.tabOutlineColor, MERCH_STYLE_DEFAULTS.tabOutlineColor),
    tabTextColor: merchColorOr(obj.tabTextColor, MERCH_STYLE_DEFAULTS.tabTextColor),
    changeAnimation: merchChangeAnimationOr(obj.changeAnimation, MERCH_STYLE_DEFAULTS.changeAnimation),
    tweenMs: merchTweenMsOr(obj.tweenMs, MERCH_STYLE_DEFAULTS.tweenMs),
    showVariants: obj.showVariants !== false,
    squarePhotos: obj.squarePhotos === true,
    offsiteMarkColor: merchColorOr(obj.offsiteMarkColor, MERCH_STYLE_DEFAULTS.offsiteMarkColor),
    textGlowPx: merchTextGlowPxOr(obj.textGlowPx, MERCH_STYLE_DEFAULTS.textGlowPx),
    textGlowColor: merchColorOr(obj.textGlowColor, MERCH_STYLE_DEFAULTS.textGlowColor),
    textGlowArrows: obj.textGlowArrows !== false,
    outerPadX: merchPadXOr(obj.outerPadX, MERCH_STYLE_DEFAULTS.outerPadX),
    boardPadX: merchPadXOr(obj.boardPadX, MERCH_STYLE_DEFAULTS.boardPadX),
    shopLabelColor: merchColorOr(obj.shopLabelColor, MERCH_STYLE_DEFAULTS.shopLabelColor),
    ctaColor: merchColorOr(obj.ctaColor, MERCH_STYLE_DEFAULTS.ctaColor),
    outerPadTop: merchPadXOr(obj.outerPadTop, MERCH_STYLE_DEFAULTS.outerPadTop),
    outerPadBottom: merchPadXOr(obj.outerPadBottom, MERCH_STYLE_DEFAULTS.outerPadBottom),
    boardBgImage: typeof obj.boardBgImage === 'string' ? obj.boardBgImage : '',
    productImageHeight: merchPhotoHeightOrNull(obj.productImageHeight),
    rowBorderColor: merchColorOr(obj.rowBorderColor, MERCH_STYLE_DEFAULTS.rowBorderColor),
    collapseXSize: merchCollapseXSizeOr(obj.collapseXSize, MERCH_STYLE_DEFAULTS.collapseXSize),
    collapseXColor: merchColorOr(obj.collapseXColor, MERCH_STYLE_DEFAULTS.collapseXColor)
  }
}
